import React, { useMemo, useState } from "react";

import "./WealthSeriesSignUp.scss";
import { Button, Modal } from "@material-ui/core";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import clsx from "clsx";
import moment from "moment";

import { useAuth } from "../../contexts/Auth";
import { ownsWealthSeriesTier } from "../../contexts/Auth/wealthSeriesTiers";
import BeautifulSkeleton from "../common/Skeletons/BeautifulSkeleton";
import ContactForm from "../EmeraldSignup/ContactForm";
import {
  TIERS,
  WS_PRODUCT_TYPE,
  lockupMaskStyle,
  tierLogoStyle,
  wsMarkStyle,
} from "./tiers";

const INTRO =
  "Leverage on cutting-edge AI technology coupled with advanced diversification techniques to unlock maximum performance and resilience. Every portfolio strategically diversifies across sectors and narratives, providing you with more robust protection and growth potential.";

const DISCLAIMER =
  "This website provides AI-powered insights for informational purposes only. Please use at your own discretion, and consult a qualified financial advisor for personalized guidance.";

/* Wordmark: the artwork as a mask filled with the tier gradient. Core has no
 * artwork of its own, so it is the WS mark from the lockup plus "CORE" set in
 * Metropolis, like the prototype. */
const TierLogo = ({ tier }) =>
  tier.id === "core" ? (
    <span className="wss__logo wss__logo--core" role="img" aria-label="Core">
      <span className="wss__ws-mark" aria-hidden="true" style={wsMarkStyle} />
      <span className="wss__core-word" aria-hidden="true">
        CORE
      </span>
    </span>
  ) : (
    <span
      className="wss__logo"
      role="img"
      aria-label={tier.name}
      style={tierLogoStyle(tier.id)}
    />
  );

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <path
      d="M2.5 6.2 5 8.6l4.6-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * One tier card. Owned with a page: a link to it. Owned without a page yet
 * (the v3 tiers): a plain card with the Subscribed chip. Not owned: a button
 * that opens Contact Sales for that tier.
 */
const TierCard = ({ tier, owned, onRequest }) => {
  const body = (
    <>
      <span className="wss__glow" aria-hidden="true" />
      <span className="wss__card-top">
        <TierLogo tier={tier} />
        {owned && (
          <span className="wss__chip">
            <CheckIcon />
            Subscribed
          </span>
        )}
      </span>
      <span className="wss__tagline">{tier.tagline}</span>
      <dl className="wss__facts">
        <div className="wss__fact">
          <dt>Focus</dt>
          <dd>{tier.focus}</dd>
        </div>
        <div className="wss__fact">
          <dt>Portfolios</dt>
          <dd>{tier.portfolios}</dd>
        </div>
      </dl>
    </>
  );
  const className = clsx("wss__card", owned && "wss__card--owned");
  const style = { "--tier-g": tier.gradient, "--tier-c": tier.glow };

  if (owned && tier.href) {
    return (
      <Link
        to={tier.href}
        className={className}
        style={style}
        data-tier={tier.id}
      >
        {body}
      </Link>
    );
  }
  if (owned) {
    return (
      <div className={className} style={style} data-tier={tier.id}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={className}
      style={style}
      data-tier={tier.id}
      aria-haspopup="dialog"
      onClick={() => onRequest(tier.productType)}
    >
      {body}
    </button>
  );
};

/**
 * Wealth Series paywall (/wealth-series-signup): the hero, Contact Sales and
 * all eight tiers (TRAD-1316, TRAD-1317). One dark design in both app themes,
 * from the dashboard prototype. A locked tier opens Contact Sales for that
 * tier; the plain button asks about Wealth Series; owned tiers link to their
 * page when one exists. Every request holds the plain button for a week
 * (userSettings.wsContact), like the other paywalls.
 */
const WealthSeriesSignUp = ({ userSettings }) => {
  const auth = useAuth();
  const { loading } = auth;
  // The sales form is open while a product type is set.
  const [contactProductType, setContactProductType] = useState(null);
  const handleClose = () => setContactProductType(null);

  const hasSubmitted = useMemo(() => {
    const submitDate = userSettings?.wsContact;
    return submitDate
      ? moment().isBefore(moment(submitDate).add(1, "week"))
      : false;
  }, [userSettings]);

  return (
    <div className="wss">
      <section className="wss__hero">
        <div
          className="wss__lockup"
          role="img"
          aria-label="TradeAlgo Wealth Series"
        >
          <span className="wss__lockup-mask" style={lockupMaskStyle} />
        </div>
        <p className="wss__intro">{INTRO}</p>
        {loading ? (
          <BeautifulSkeleton
            className="no-transform-scale"
            width={163}
            height={48}
          />
        ) : (
          <Button
            variant="contained"
            className="wss__cta"
            onClick={() => setContactProductType(WS_PRODUCT_TYPE)}
            disabled={hasSubmitted}
          >
            {hasSubmitted ? "Access Requested" : "Contact Sales"}
          </Button>
        )}
      </section>

      <section className="wss__tiers">
        <h2 className="wss__tiers-title">Choose a tier</h2>
        <div className="wss__grid">
          {TIERS.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              owned={ownsWealthSeriesTier(auth, tier.id)}
              onRequest={setContactProductType}
            />
          ))}
        </div>
        <p className="wss__disclaimer">{DISCLAIMER}</p>
      </section>

      <Modal open={contactProductType !== null} onClose={handleClose}>
        {/* MUI v4 TrapFocus needs a child that can hold a ref; the connected
            ContactForm cannot, so focus (and Escape) would stay behind it. */}
        <div tabIndex={-1} className="wss__modal-focus">
          <ContactForm
            product_type={contactProductType || WS_PRODUCT_TYPE}
            onClose={handleClose}
          />
        </div>
      </Modal>
    </div>
  );
};

const stateToProps = (state) => ({
  userSettings: state.userSettings.userSettings,
});

export default connect(stateToProps)(WealthSeriesSignUp);
